'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { env } from '@/config/env';
import axios from 'axios';
import { isRefreshing, refreshPromise } from '@/lib/api/client';
import { resyncConversations, resyncMessages } from '@/lib/realtime/delta-sync';

// Pull delta for conversations + every message thread the user has open
// (up to a small cap to bound work). Used on socket reconnect and on
// visibility/online recovery — both are points where events may have
// been missed while we were offline or the tab was process-suspended.
const MAX_CONVERSATIONS_TO_RESYNC = 10;

function resyncOnReconnect(queryClient: QueryClient, activeConversationId: string | null) {
  resyncConversations(queryClient).catch(() => {});
  // Find every ['messages', convId] query in the cache. Prioritize the
  // currently active conversation, then the most-recently touched ones
  // (React Query stamps `dataUpdatedAt` on each query state).
  const cache = queryClient.getQueryCache();
  const messageQueries = cache.getAll().filter((q) => {
    const key = q.queryKey;
    return Array.isArray(key) && key[0] === 'messages' && typeof key[1] === 'string';
  });
  const ranked = messageQueries
    .map((q) => ({
      convId: q.queryKey[1] as string,
      updatedAt: q.state.dataUpdatedAt ?? 0,
    }))
    .sort((a, b) => {
      if (a.convId === activeConversationId) return -1;
      if (b.convId === activeConversationId) return 1;
      return b.updatedAt - a.updatedAt;
    })
    .slice(0, MAX_CONVERSATIONS_TO_RESYNC);
  for (const { convId } of ranked) {
    resyncMessages(queryClient, convId).catch(() => {});
  }
}

interface PendingMessage {
  id: string;
  type: string;
  content: { text?: string; url?: string; mimetype?: string; filename?: string };
  quotedMessageId?: string;
  // Lets the receiving tab / agent render attribution on the optimistic
  // bubble before the authoritative `message:new` arrives.
  senderUserId?: string;
}

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  subscribeToChannel: (channelId: string) => void;
  unsubscribeFromChannel: (channelId: string) => void;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  broadcastPendingMessage: (conversationId: string, message: PendingMessage) => void;
  // Mark a conversation as read via the socket (saves the CORS preflight
  // + round-trip of POST /conversations/:id/read). Returns true if the
  // socket handled it; callers fall back to the REST endpoint when false.
  markConversationRead: (conversationId: string) => boolean;
  // Refresh the active-agent lease. Safe to fire on any cadence — the
  // server only bumps activeAgentSince if the caller already holds the
  // claim, so it never steals.
  sendAgentHeartbeat: (conversationId: string) => void;
  // Supervisor shadow mode (read-only, invisible-to-agents view).
  // Role-gated on the server (SUPERVISOR / ADMIN / OWNER); non-privileged
  // emits are silently rejected with an `error` event.
  shadowJoinConversation: (conversationId: string) => void;
  shadowLeaveConversation: (conversationId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  isConnected: false,
  subscribeToChannel: () => {},
  unsubscribeFromChannel: () => {},
  joinConversation: () => {},
  leaveConversation: () => {},
  startTyping: () => {},
  stopTyping: () => {},
  broadcastPendingMessage: () => {},
  markConversationRead: () => false,
  sendAgentHeartbeat: () => {},
  shadowJoinConversation: () => {},
  shadowLeaveConversation: () => {},
});

export function useWebSocket() {
  return useContext(WebSocketContext);
}

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const socketRef = useRef<Socket | null>(null);
  const firstConnectRef = useRef(true);
  const queryClient = useQueryClient();
  const activeConversationRef = useRef<string | null>(null);

  // Re-create the socket only on login/logout. Token rotations update
  // socket.auth in place (see effect below) instead of tearing down the
  // connection — otherwise proactive refresh every ~14min would churn
  // the socket and make every rotation visibly non-seamless.
  useEffect(() => {
    const initialToken = useAuthStore.getState().tokens?.accessToken;
    if (!isAuthenticated || !initialToken) {
      // Disconnect if not authenticated
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection (use WS_URL, not API_URL which has /api/v1 suffix)
    // OPTIMIZED: Faster reconnection for better UX during network hiccups
    const newSocket = io(env.NEXT_PUBLIC_WS_URL, {
      auth: {
        token: initialToken,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 15,     // More attempts before giving up (was 10)
      reconnectionDelay: 500,        // Faster initial retry (was 1000ms)
      reconnectionDelayMax: 3000,    // Faster max delay (was 5000ms)
      timeout: 20000,                // Connection timeout
      forceNew: false,               // Reuse existing connection when possible
    });

    socketRef.current = newSocket;

    // Connection events
    newSocket.on('connect', () => {
      console.debug('WebSocket connected');
      setIsConnected(true);
      // First connect is covered by the normal cold-start queries;
      // subsequent connects are reconnects → delta-resync what changed
      // while we were offline.
      if (firstConnectRef.current) {
        firstConnectRef.current = false;
      } else {
        resyncOnReconnect(queryClient, activeConversationRef.current);
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.debug('WebSocket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', async (error) => {
      console.error('WebSocket connection error:', error.message);
      setIsConnected(false);

      // Prefer the structured code the backend now attaches to socket auth errors;
      // fall back to substring match so a stale frontend keeps working during rollout.
      const errCode = (error as Error & { data?: { code?: string } }).data?.code;
      const isAuthError =
        errCode === 'TOKEN_EXPIRED' ||
        errCode === 'INVALID_TOKEN' ||
        error.message.includes('authentication') ||
        error.message.includes('jwt') ||
        error.message.includes('token');

      // If auth error, try to refresh token (use shared lock to prevent parallel refreshes with API client)
      if (isAuthError) {
        console.debug('Socket auth failed, attempting token refresh...');
        try {
          let newAccessToken: string;

          if (isRefreshing && refreshPromise) {
            // API client is already refreshing — wait for it instead of doing a parallel refresh
            newAccessToken = await refreshPromise;
          } else {
            // No refresh in progress — do our own
            const refreshToken = useAuthStore.getState().tokens?.refreshToken;
            if (!refreshToken) throw new Error('No refresh token');

            const API_URL = env.NEXT_PUBLIC_API_URL || 'http://localhost:4000/api/v1';
            const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
            const newTokens = response.data;
            useAuthStore.getState().setTokens(newTokens);
            newAccessToken = newTokens.accessToken;
          }

          // Update socket auth and reconnect with fresh token
          newSocket.auth = { token: newAccessToken };
          newSocket.connect();
          console.debug('Token refreshed, reconnecting socket...');
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          useAuthStore.getState().logout();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
      }
    });

    setSocket(newSocket);

    // Cleanup on unmount — remove all listeners to prevent memory leaks
    return () => {
      newSocket.off('connect');
      newSocket.off('disconnect');
      newSocket.off('connect_error');
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated]);

  // When the access token rotates (proactive refresh or 401 retry), update
  // the socket's auth in place so any future reconnection uses the latest
  // token — but do NOT disconnect the currently-open socket. The server
  // validated the token at handshake; it stays trusted for the session.
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state) => {
      const current = socketRef.current;
      if (current && state.tokens?.accessToken) {
        current.auth = { token: state.tokens.accessToken };
      }
    });
    return unsubscribe;
  }, []);

  // Chrome and iOS Safari aggressively suspend background tabs. The
  // socket can stay nominally "connected" while the process is parked,
  // causing events to be dropped upstream. Pull a delta on visibility
  // return and on `online` to backfill anything missed. This is in
  // addition to the socket-reconnect resync — the two triggers are
  // complementary: reconnect fires on transport drop, visibility fires
  // on process wake.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    let lastResyncAt = 0;
    const COOLDOWN_MS = 3000; // avoid piling on during rapid visibility toggles
    const trigger = () => {
      if (!socketRef.current?.connected) return;
      const now = Date.now();
      if (now - lastResyncAt < COOLDOWN_MS) return;
      lastResyncAt = now;
      resyncOnReconnect(queryClient, activeConversationRef.current);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') trigger();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', trigger);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', trigger);
    };
  }, [queryClient]);

  // Presence heartbeat (2 Sep 2026): the rotation hands first-hour rows only
  // to agents who are HERE. "Here" is a visible tab: emit while visible, on
  // return to visibility and on focus; never while hidden, so a tab parked
  // all day does not read as an agent on duty. The server keys a 120 s
  // presence entry per beat.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      if (socketRef.current?.connected) socketRef.current.emit('agent:heartbeat', {});
    };
    beat();
    const id = setInterval(beat, 60_000);
    document.addEventListener('visibilitychange', beat);
    window.addEventListener('focus', beat);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', beat);
      window.removeEventListener('focus', beat);
    };
  }, []);

  const subscribeToChannel = useCallback((channelId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('channel:subscribe', { channelId });
    }
  }, []);

  const unsubscribeFromChannel = useCallback((channelId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('channel:unsubscribe', { channelId });
    }
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    activeConversationRef.current = conversationId;
    if (socketRef.current) {
      socketRef.current.emit('conversation:join', { conversationId });
    }
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    if (activeConversationRef.current === conversationId) {
      activeConversationRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.emit('conversation:leave', { conversationId });
    }
  }, []);

  const startTyping = useCallback((conversationId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('typing:start', { conversationId });
    }
  }, []);

  const stopTyping = useCallback((conversationId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('typing:stop', { conversationId });
    }
  }, []);

  // Broadcast pending message to other agents in the conversation (shared optimistic UI)
  const broadcastPendingMessage = useCallback((conversationId: string, message: PendingMessage) => {
    if (socketRef.current) {
      socketRef.current.emit('message:pending', { conversationId, message });
    }
  }, []);

  const markConversationRead = useCallback((conversationId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('conversation:read', { conversationId });
      return true;
    }
    return false;
  }, []);

  const sendAgentHeartbeat = useCallback((conversationId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('agent:heartbeat', { conversationId });
    }
  }, []);

  const shadowJoinConversation = useCallback((conversationId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('conversation:shadow-join', { conversationId });
    }
  }, []);

  const shadowLeaveConversation = useCallback((conversationId: string) => {
    if (socketRef.current) {
      socketRef.current.emit('conversation:shadow-leave', { conversationId });
    }
  }, []);

  // Memoize the context value so that consumers don't re-render on every
  // WebSocketProvider render. The callbacks are all useCallback([], ...)
  // stable, so `contextValue` is effectively stable except when `socket`
  // or `isConnected` actually change (connect / disconnect events). This
  // is belt-and-suspenders around the inbox-mount observer cascade —
  // fewer gratuitous re-renders = fewer useQuery observer notifications
  // in the critical window.
  const contextValue = useMemo(
    () => ({
      socket,
      isConnected,
      subscribeToChannel,
      unsubscribeFromChannel,
      joinConversation,
      leaveConversation,
      startTyping,
      stopTyping,
      broadcastPendingMessage,
      markConversationRead,
      sendAgentHeartbeat,
      shadowJoinConversation,
      shadowLeaveConversation,
    }),
    [
      socket,
      isConnected,
      subscribeToChannel,
      unsubscribeFromChannel,
      joinConversation,
      leaveConversation,
      startTyping,
      stopTyping,
      broadcastPendingMessage,
      markConversationRead,
      sendAgentHeartbeat,
      shadowJoinConversation,
      shadowLeaveConversation,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}
